import { Component, computed, input, output, viewChild } from '@angular/core';
import { NumericTextBoxComponent } from '@progress/kendo-angular-inputs';

export interface PagingOptions {
  disabled?: boolean;
  page: number;
  totalPages: number;
  goNextItem: () => void;
  goPreviousItem: () => void;
}

function toValidPage(value: number): number {
  return Number.isFinite(value) ? value : 1;
}
function toValidTotalPages(value: number): number {
  return value > 0 ? value : 1;
}

@Component({
  standalone: false,
  selector: 'ti-paging-input',
  templateUrl: 'paging-input.component.html',
  styleUrls: ['paging-input.component.sass'],
})
export class PagingInputComponent {
  readonly page = input(1, { transform: toValidPage });
  readonly totalPages = input(1, { transform: toValidTotalPages });
  readonly disabled = input(false);

  readonly pageChange = output<number>();
  readonly goNext = output<void>();
  readonly goPrev = output<void>();

  private readonly pageInput = viewChild(NumericTextBoxComponent);

  readonly isPrevDisabled = computed(() => this.page() <= 1);
  readonly isNextDisabled = computed(() => this.page() >= this.totalPages());

  onPageInputKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Enter') {
      return;
    }

    this.navigateTo(this.pageInput()?.value ?? this.page());
  };

  goNextPage = (): void => {
    this.navigateTo(this.page() + 1);
    this.goNext.emit();
  };

  goPrevPage = (): void => {
    this.navigateTo(this.page() - 1);
    this.goPrev.emit();
  };

  private navigateTo = (pageNumber: number): void => {
    if (!Number.isFinite(pageNumber)) {
      return;
    }

    const nextPage = Math.min(this.totalPages(), Math.max(1, pageNumber));
    if (nextPage !== this.page()) {
      this.pageChange.emit(nextPage);
    }
  };
}
